import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import COLORS from '../../theme/colors';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setError('');
    setMessage('');
    setLoading(true);

    try {
      await resetPassword(email);
      setMessage('Password reset email sent! Check your inbox and follow the instructions to reset your password.');
    } catch (error) {
      console.error('Password reset error:', error);
      
      switch (error.code) {
        case 'auth/user-not-found':
          setError('No account found with this email address.');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address.');
          break;
        case 'auth/too-many-requests':
          setError('Too many requests. Please wait a moment before trying again.');
          break;
        default:
          setError('Failed to send password reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: COLORS.background.light }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: 4, boxShadow: '0 2px 16px rgba(44, 62, 80, 0.08)', maxWidth: 400, width: '100%' }}>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/login')}
            sx={{ 
              color: COLORS.text.secondary, 
              textTransform: 'none', 
              fontSize: 14,
              mb: 2,
              p: 0,
              minWidth: 0
            }}
          >
            Back to Login
          </Button>
          <Typography variant="h5" fontWeight={600} sx={{ color: COLORS.text.primary, textAlign: 'left' }}>
            Reset Password
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.text.secondary, mt: 1 }}>
            Enter your email address and we'll send you a link to reset your password.
          </Typography>
        </Box>

        {message ? (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              {message}
            </Alert>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ mb: 2, color: COLORS.text.secondary }}>
                Didn't receive the email? Check your spam folder or try again.
              </Typography>
              <Button
                variant="text"
                onClick={() => {
                  setMessage('');
                  setError('');
                }}
                sx={{ color: COLORS.primary.main, textTransform: 'none', fontWeight: 600 }}
              >
                Send Another Email
              </Button>
            </Box>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              margin="normal"
              sx={{ 
                background: COLORS.background.paper, 
                borderRadius: 2, 
                mb: 2 
              }}
              InputProps={{ 
                sx: { fontSize: 18, color: COLORS.text.primary } 
              }}
              InputLabelProps={{ 
                sx: { fontSize: 16, color: COLORS.text.secondary } 
              }}
            />
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ 
                mt: 1, 
                mb: 2, 
                background: COLORS.primary.main, 
                color: COLORS.primary.contrastText, 
                borderRadius: 3, 
                fontWeight: 600, 
                fontSize: 18, 
                boxShadow: 'none', 
                '&:hover': { background: COLORS.primary.dark } 
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Email'}
            </Button>
          </form>
        )}

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" sx={{ color: COLORS.text.secondary }}>
            Remember your password?{' '}
            <Link
              component="button"
              type="button"
              onClick={() => navigate('/login')}
              sx={{ 
                color: COLORS.primary.main, 
                textDecoration: 'none',
                fontWeight: 600,
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              Sign In
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default ForgotPassword;